import HOMEPAGE_SEARCH_INDEX_CONFIG from '@/app/config/homepage'
import { ROUTES } from '@/app/lib/constants'
import { loadLocaleFiles } from '@/app/lib/load-locale'
import { CoursesPage } from '@/app/modules/course/pages'
import { ExternalCoursesPage } from '@/app/modules/course/pages/ExternalCoursesPage'
import { generateTypesenseScopedKey } from '@/app/modules/search/lib/keys'
import { isBoom } from '@hapi/boom'

type ScopedApiKeys = {
  subcategories_courses: string
  categories: string
}

// This is the API key used to generate scoped search keys - it should NOT be exposed to the client
const frontendSearchApiKey = process.env.NEXT_SEARCH_INDEX_FRONTEND_API_KEY

export async function getServerSideProps(context) {
  const { locale } = context

  try {
    const messages = await loadLocaleFiles(context.locale, [
      'strings',
      'search-page',
      'navigation',
    ])

    if (HOMEPAGE_SEARCH_INDEX_CONFIG.enabled) {
      if (!frontendSearchApiKey) {
        throw new Error('NEXT_SEARCH_INDEX_FRONTEND_API_KEY is not configured')
      }

      const scopedApiKeys: ScopedApiKeys = {
        // Filter to only show things that have published courses
        subcategories_courses: generateTypesenseScopedKey(
          frontendSearchApiKey,
          '{"filter_by":"$courses(published:=true)"}'
        ),
        // Filter to only show active categories that have published courses
        categories: generateTypesenseScopedKey(
          frontendSearchApiKey,
          JSON.stringify({
            filter_by:
              '$subcategories(is_active:=true&&$subcategories_courses($courses(published:=true)))',
            exclude_fields:
              '$subcategories($subcategories_courses(course_id,subcategory_id,$courses(*)))',
          })
        ),
      }

      return {
        props: {
          messages,
          scopedApiKeys,
        },
      }
    }

    return {
      props: {
        messages,
      },
    }
  } catch (e) {
    console.error(e)
    if (isBoom(e, 401)) {
      return {
        redirect: {
          destination: ROUTES.logout,
          permanent: false,
          locale,
        },
      }
    } else if (isBoom(e, 403)) {
      return {
        redirect: {
          destination: `/${locale}${ROUTES.accessDenied}`,
          permanent: false,
          locale,
        },
      }
    }

    throw e
  }
}

type HomePageProps = {
  scopedApiKeys?: ScopedApiKeys
}

export default function HomePage({ scopedApiKeys }: HomePageProps) {
  if (HOMEPAGE_SEARCH_INDEX_CONFIG.enabled) {
    if (!scopedApiKeys) {
      throw new Error('scopedApiKeys is required when search is enabled')
    }
    return <ExternalCoursesPage scopedApiKeys={scopedApiKeys} />
  }

  return <CoursesPage />
}
